import React, { useState } from 'react';

interface LobbyViewProps {
  onJoinRoom: (serverURL: string, roomCode: string, playerName: string) => void;
  onCreateRoom: (serverURL: string, playerName: string) => void;
  onPlayAI?: (serverURL: string, playerName: string) => void;
  isConnected: boolean;
}

export const LobbyView: React.FC<LobbyViewProps> = ({ 
  onJoinRoom, 
  onCreateRoom,
  onPlayAI, 
  isConnected 
}) => {
  const [roomCode, setRoomCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [serverURL] = useState('wss://api.thecontactgame.com/ws');
  // const [serverURL] = useState('ws://localhost:8080/ws'); // Use this for local development

  const isPlayerNameValid = (name: string) => {
    const trimmed = name.trim();
    return trimmed.length >= 2 && trimmed.length <= 50;
  };

  const isRoomCodeValid = (code: string) => {
    const trimmed = code.trim();
    return trimmed.length === 6;
  };

  const handleJoinRoom = () => {
    if (isPlayerNameValid(playerName) && isRoomCodeValid(roomCode)) {
      onJoinRoom(serverURL, roomCode, playerName);
    }
  };

  const handleCreateRoom = () => {
    if (isPlayerNameValid(playerName)) {
      onCreateRoom(serverURL, playerName);
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px'
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px'
      }}>
        <h1 style={{
          fontSize: '40px',
          fontWeight: 'bold',
          color: 'white',
          textAlign: 'center',
          margin: 0
        }}>
          TheContactGame
        </h1>
        <p style={{
          fontSize: '18px',
          color: 'rgba(255, 255, 255, 0.8)',
          margin: 0
        }}>
          Word Chain Party Game
        </p>
      </div>

      <div style={{
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <input
          type="text"
          placeholder="Enter Your Name"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          style={{
            padding: '16px',
            fontSize: '18px',
            borderRadius: '12px',
            border: 'none',
            outline: 'none',
            background: 'white',
            color: 'black'
          }}
        />

        <div style={{ height: '50px' }} />

        <input
          type="text"
          placeholder="Room Code"
          value={roomCode}
          onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
          maxLength={6}
          style={{
            padding: '16px',
            fontSize: '18px',
            borderRadius: '12px',
            border: 'none',
            outline: 'none',
            background: 'white',
            color: 'black',
            textTransform: 'uppercase'
          }}
        />

        <button
          onClick={handleJoinRoom}
          disabled={!isPlayerNameValid(playerName) || !isRoomCodeValid(roomCode)}
          style={{
            padding: '16px',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '12px',
            border: 'none',
            background: isPlayerNameValid(playerName) && isRoomCodeValid(roomCode) 
              ? '#10B981' 
              : 'rgba(128, 128, 128, 0.5)',
            color: 'white',
            cursor: isPlayerNameValid(playerName) && isRoomCodeValid(roomCode) 
              ? 'pointer' 
              : 'not-allowed',
            transition: 'all 0.2s'
          }}
        >
          Join Room
        </button>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '4px 0'
        }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.3)' }} />
          <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.3)' }} />
        </div>

        <button
          onClick={handleCreateRoom}
          disabled={!isPlayerNameValid(playerName)}
          style={{
            padding: '16px',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '12px',
            border: 'none',
            background: isPlayerNameValid(playerName) 
              ? 'rgba(59, 130, 246, 0.8)' 
              : 'rgba(128, 128, 128, 0.5)',
            color: 'white',
            cursor: isPlayerNameValid(playerName) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <span style={{ fontSize: '20px' }}>+</span>
          Create New Room
        </button>

        {onPlayAI && (
          <button
            onClick={() => isPlayerNameValid(playerName) && onPlayAI(serverURL, playerName)}
            disabled={!isPlayerNameValid(playerName)}
            style={{
              padding: '16px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '12px',
              border: 'none',
              background: isPlayerNameValid(playerName) 
                ? 'rgba(147, 51, 234, 0.8)' 
                : 'rgba(128, 128, 128, 0.5)',
              color: 'white',
              cursor: isPlayerNameValid(playerName) ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '20px' }}>🤖</span>
            Play against AI
          </button>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: 'center',
          marginTop: '8px'
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: isConnected ? '#10B981' : '#6B7280'
          }} />
          <span style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      <div style={{ flex: 1 }} />
    </div>
  );
};
